import { useUnsaved } from "@/lib/unsaved";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Shield, Save, X, Plus, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { useGuild } from "./DashboardLayout";
import { PageHead } from "@/components/kit";

export default function BotMasters() {
  const { guildId } = useGuild();
  const [cfg, setCfg] = useState(null);
  const save = useUnsaved(cfg, setCfg, () => saveRaw());
  const [input, setInput] = useState("");

  useEffect(() => { api.get(`/guilds/${guildId}/modules/botmasters`).then((r) => setCfg(r.data)); }, [guildId]);
  if (!cfg) return <p className="text-zinc-500">Loading…</p>;
  const roles = cfg.config.roles || [];

  const add = () => {
    const v = input.trim().replace(/^@/, "");
    if (!v || roles.includes(v)) return;
    setCfg({ ...cfg, config: { ...cfg.config, roles: [...roles, v] } });
    setInput("");
  };
  const remove = (r) => setCfg({ ...cfg, config: { ...cfg.config, roles: roles.filter((x) => x !== r) } });
  const saveRaw = async () => {
    await api.put(`/guilds/${guildId}/modules/botmasters`, { enabled: true, config: cfg.config });
    toast.success("Bot Masters saved");
  };

  return (
    <div>
      <PageHead title="Bot Masters" desc="Discord roles that can administer Crank.live on this server.">
        <button data-testid="botmasters-save" onClick={save} className="bg-volt text-volt-ink font-semibold rounded-md px-5 py-2 hover:bg-volt-hover flex items-center gap-2"><Save className="w-4 h-4" /> Save</button>
      </PageHead>

      <div className="max-w-2xl space-y-6">
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-200/90">Bot Masters have admin-level power over the bot. Any Discord role with the <b>Administrator</b> permission is automatically a Bot Master.</p>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-4">
          <h3 className="font-head font-semibold flex items-center gap-2"><Shield className="w-4 h-4 text-volt" /> Additional Bot Master roles</h3>
          <div className="flex gap-2">
            <input data-testid="botmasters-input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
              placeholder="Type a role name, e.g. Moderator" className="flex-1 bg-black border border-zinc-800 rounded-md px-3 py-2 outline-none focus:border-volt" />
            <button data-testid="botmasters-add" onClick={add} className="bg-zinc-800 hover:bg-zinc-700 rounded-md px-4 flex items-center gap-1 text-sm"><Plus className="w-4 h-4" /> Add</button>
          </div>
          <div className="flex flex-wrap gap-2">
            {roles.length === 0 && <p className="text-sm text-zinc-600">No additional roles yet. Add roles that should manage the bot even without Administrator.</p>}
            {roles.map((r) => (
              <span key={r} data-testid={`botmaster-role-${r}`} className="flex items-center gap-1.5 bg-volt/10 text-volt rounded-full pl-3 pr-1.5 py-1 text-sm">
                {r}
                <button onClick={() => remove(r)} className="hover:bg-volt/20 rounded-full p-0.5"><X className="w-3.5 h-3.5" /></button>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
